import firebase from 'firebase/app'
import { capitalize } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Loading } from '../../components'
import { openSnackbar } from '../../ducks/snackbar/actions'
import {
  asyncError,
  asyncFinish,
  asyncStart,
  initialize,
} from '../../ducks/ui/actions'
import { auth, firestore } from '../../firebase-init'
import { IStoreState } from '../../store/types'
import {
  createAndGetUserDocument,
  ICircle,
  IFirestoreContext,
} from 'daily-dose-common'

import { collectIdAndDocs } from '../../utilities'

export const FirestoreContext = React.createContext({} as IFirestoreContext)

const FirestoreProvider = ({ children }: any) => {
  const dispatch = useDispatch()
  const initialized = useSelector((state: IStoreState) => state.ui.initialized)
  const [currentUser, setCurrentUser] = useState(null)
  const [myCircles, setMyCircles] = useState([] as ICircle[])
  const [hasFetchedMyCircles, setHasFetchedMyCircles] = useState(false)
  const [username, setUsername] = useState(null)

  useEffect(() => {
    const unsubscribeFromAuth = auth.onAuthStateChanged(
      async (userAuth: firebase.User | null) => {
        try {
          if (userAuth) {
            dispatch(asyncStart('USER-FETCHING'))
            const userRef = await createAndGetUserDocument(userAuth)
            userRef.onSnapshot((snapshot) => {
              setCurrentUser({
                id: snapshot.id,
                ...snapshot.data(),
              })
              // TODO: Set a variable hasFetchedUser to identify when the user has been fetched
              setUsername(snapshot.data()?.username)
              dispatch(asyncFinish('USER-FOUND'))
              dispatch(initialize())
              // TODO: Replace for isUserLoggedIn or something like that
              const username = snapshot.data()?.username as string
              if (username) {
                // TODO: Move to utils
                const nameArray = snapshot.data()?.displayName?.split(' ')
                const firstName = nameArray && nameArray[0]
                const message = `Welcome to the Daily Dose, ${capitalize(
                  firstName || username
                )}`
                dispatch(openSnackbar(message, 'success'))
              }
            })
          } else {
            setUsername(null)
            dispatch(initialize())
          }

          // TODO: May not need this
          setCurrentUser(currentUser)
        } catch (error) {
          dispatch(asyncError(error))
          console.warn('Could not retrieve user ', error)
        }
      }
    )

    return () => {
      unsubscribeFromAuth()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // TODO: Double check this because we do not want to send user to my circles until hacer has a valid username
    // TODO: Pluss check firebase rules
    if (!username) return

    dispatch(asyncStart('MY-CIRCLES-FETCHING'))
    const unsubscribeFromFirestore = firestore
      .collection('circles')
      .where('membersUsernames', 'array-contains', username)
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snapshot) => {
          const myCircles = snapshot.docs.map(collectIdAndDocs)
          setMyCircles(myCircles)
          setHasFetchedMyCircles(true)
          dispatch(asyncFinish('MY-CIRCLES-FOUND'))
        },
        (error) => {
          dispatch(asyncError(error.message))
          dispatch(openSnackbar('Oppps. We could not retrieve your groups', 'error'))
          setHasFetchedMyCircles(false)
          console.warn('Error retrieven groups ', error)
        }
      )

    return () => {
      unsubscribeFromFirestore()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username])

  return (
    <FirestoreContext.Provider
      value={{
        currentUser,
        myCircles,
        username,
        hasFetchedMyCircles,
      }}
    >
      {!initialized ? <Loading /> : children}
    </FirestoreContext.Provider>
  )
}

/**
 * Firestore Hook
 */
const useFirestore = () => {
  const context = useContext(FirestoreContext)

  if (context === undefined) {
    throw new Error('useFirestore msut be used within FirestoreProvider')
  }
  return context
}

export { FirestoreProvider, useFirestore }
